import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorState } from '@/components/ui/error-state';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  /** Custom fallback. Receives the error and a reset callback. */
  fallback?: (error: unknown, reset: () => void) => ReactNode;
  /** Remount the boundary when this changes — e.g. the current pathname. */
  resetKey?: unknown;
}

interface State {
  error: unknown;
}

/**
 * Catches render-time errors and shows <ErrorState> instead of a white screen.
 *
 * A blank page is the worst failure mode this app has short of a wrong dose:
 * the user cannot tell "the app crashed" from "you have nothing to take today",
 * and there is no way out of it. This at least names the problem and offers a
 * reload.
 *
 * It does NOT catch errors thrown in event handlers or async callbacks — React
 * error boundaries never have. Those paths are covered by TanStack Query error
 * states and `toast.error` on mutations.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Self-hosted: there is no error reporting service to call, so the console
    // is genuinely where this goes. Keep the component stack; it is the only
    // breadcrumb someone debugging on a phone will have.
    // eslint-disable-next-line no-console
    console.error('[medtrack] render error', error, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    if (this.state.error !== null && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas px-4">
        <ErrorState
          title="The app hit an unexpected problem"
          error={error}
          onRetry={this.reset}
          className="w-full max-w-md"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload the app
          </Button>
        </ErrorState>
      </div>
    );
  }
}

export default ErrorBoundary;
